import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut, FileText, Plus, Home, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/admin/blogs", label: "Blogs", icon: FileText, end: false },
  { to: "/admin/blogs/new", label: "New Post", icon: Plus, end: false },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-[100svh] bg-bg-0 text-white flex" data-testid="admin-layout">
      <aside className="w-64 border-r border-white/[0.06] bg-bg-1/40 flex-shrink-0 hidden md:flex flex-col">
        <div className="p-5 border-b border-white/[0.06]">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="relative w-8 h-8 rounded-lg overflow-hidden">
              <span className="absolute inset-0 bg-gradient-to-tr from-neon-magenta via-neon-orange to-neon-cyan" />
              <span className="absolute inset-[2px] rounded-md bg-bg-0 flex items-center justify-center font-mono font-extrabold text-[12px]">NW</span>
            </span>
            <div>
              <div className="font-semibold text-sm leading-tight">Novarise Works</div>
              <div className="text-[10px] text-white/40 font-mono tracking-[0.16em] uppercase">CMS · v1</div>
            </div>
          </Link>
        </div>

        <nav className="p-3 space-y-1 flex-1">
          {links.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/admin/blogs/new"}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
                    isActive
                      ? "bg-white/[0.08] text-white"
                      : "text-white/60 hover:text-white hover:bg-white/[0.04]"
                  }`
                }
              >
                <Icon size={15} />
                {l.label}
              </NavLink>
            );
          })}
          <div className="border-t border-white/[0.06] my-3" />
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/[0.04]"
          >
            <Home size={15} /> View site
          </a>
        </nav>

        <div className="p-3 border-t border-white/[0.06]">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-neon-cyan to-neon-purple flex items-center justify-center text-[11px] font-mono font-bold">
              {(user?.name || "A").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name}</div>
              <div className="text-[10px] text-white/40 truncate">{user?.email}</div>
            </div>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              aria-label="Logout"
              className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/60 hover:text-white flex items-center justify-center"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 border-b border-white/[0.06] bg-bg-0/95 backdrop-blur px-4 py-3 flex items-center justify-between">
        <Link to="/admin/blogs" className="flex items-center gap-2 font-semibold text-sm">
          <span className="w-7 h-7 rounded-md bg-gradient-to-tr from-neon-magenta to-neon-cyan" />
          Novarise CMS
        </Link>
        <button onClick={doLogout} className="text-xs text-white/60 hover:text-white flex items-center gap-1.5">
          <LogOut size={13} /> Sign out
        </button>
      </header>

      <main className="flex-1 overflow-x-hidden pt-14 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
